/**
 * Driver Themes ticket — WebGL2 utility layer.
 *
 * Pure-function helpers for the ShaderChargingAnimation component
 * (Commit 7). Owns the program cache, shader compile + link
 * pipeline, quad buffer setup, uniform binding, frame draw, and
 * the visibility-aware RAF loop.
 *
 * No React imports. No JSX. The only DOM surface touched outside
 * the WebGL2 API is `document.hidden` + `document.addEventListener`
 * for the Page Visibility API integration and `requestAnimation
 * Frame` / `cancelAnimationFrame` for the render loop. Everything
 * else flows through the caller-provided WebGL2RenderingContext.
 *
 * Cache lifecycle: compiled WebGLProgram objects live in a
 * module-level Map keyed by `<shaderKind>` or `<shaderKind>:
 * <variant>`. Programs persist across React unmounts so a theme
 * flip + flip-back doesn't pay the compile cost twice. The
 * disposeProgram helper exists for tests and hot-reload but is
 * not called on normal component unmount.
 */

// ── Types ────────────────────────────────────────────────────────────

/**
 * Tagged-tuple uniform value. The runtime dispatcher in
 * bindUniforms switches on `typeof` + array length to pick the
 * right `gl.uniformNf` call. `readonly` tuples so callers can
 * pass the `as const` ORB_UNIFORMS values from Commit 5 without
 * widening to `number[]`.
 */
export type UniformValue =
  | number
  | readonly [number, number]
  | readonly [number, number, number]
  | readonly [number, number, number, number];

/**
 * Pausable RAF loop handle returned by `createPausableRAF`. The
 * caller controls start/stop lifecycle; the helper handles the
 * Page Visibility API integration internally so the render loop
 * pauses when the tab backgrounds.
 */
export interface PausableRAF {
  /** Begin the RAF loop and attach the visibility listener.
   *  Idempotent — calling twice does not double up. */
  start: () => void;
  /** Cancel the in-flight RAF (if any) and detach the visibility
   *  listener. Idempotent. */
  stop: () => void;
}

// ── Program cache ────────────────────────────────────────────────────

/**
 * Per-GL-context WebGLProgram cache. Outer key is the GL context
 * itself (WeakMap), inner key is the shader identity string.
 *
 * WHY PER-CONTEXT:
 *
 * WebGL programs are BOUND to the GL context they were compiled in
 * — a program created against context A cannot be used by context B.
 * The original implementation used a flat `Map<string, WebGLProgram>`
 * which kept programs alive across React unmounts (correct intent:
 * theme flip + flip-back shouldn't recompile). But that flat cache
 * silently broke after the user navigated AWAY from a page hosting
 * shader cards and back: the old GL contexts were GC'd alongside
 * their canvases, but the cache still held their programs, and the
 * new mount's fresh GL contexts received those stale-context
 * programs from `getOrCreateProgram`. `gl.useProgram(staleProgram)`
 * silently fails, `getAttribLocation` returns -1, draws produce
 * nothing — visible as blank canvases after route-away-then-back.
 *
 * The WeakMap structure fixes that without losing the within-mount
 * cache benefit:
 *   - Same GL context (theme flip on same page mount) → inner Map
 *     hit, no recompile. Original perf intent preserved.
 *   - Different GL context (cross-mount remount) → no inner Map →
 *     fresh compile. Correct.
 *   - Old GL contexts get GC'd when their canvases unmount → their
 *     WeakMap entries drop automatically → no memory leak. No need
 *     for consumers to call `disposeProgram` on unmount (and
 *     ShaderChargingAnimation's existing "don't call disposeProgram
 *     on unmount" pattern stays correct, just for a slightly
 *     different reason — within-context the program is reusable;
 *     cross-context it gets recompiled anyway).
 *
 * INNER CACHE KEY CONVENTION (callers must follow):
 *   - `<shaderKind>` for single-program shaders (`'obsidian'`,
 *     `'neon'`).
 *   - `<shaderKind>:<variant>` for variant-injected shaders
 *     (e.g. base form `'orb:initiative'`).
 *   - `<shaderKind>:<variant>:steps-<n>` for the orb composite
 *     when a non-default `VOLUMETRIC_STEPS` value is injected
 *     (Commit 11 — `'orb:initiative:steps-24'` on low-end
 *     devices, `'orb:initiative:steps-32'` otherwise; same
 *     pattern for `electric-light` and `dark-electric`).
 *
 * Different `#define` values produce different compiled programs
 * and MUST have distinct cache keys; reusing the same key across
 * variants (or across step counts) would return the wrong
 * program within the same GL context. Any future compile-time
 * `#define` injection that meaningfully changes the GLSL output
 * must extend the cache key with a `:<suffix>` segment following
 * this convention.
 */
const PROGRAM_CACHE = new WeakMap<WebGL2RenderingContext, Map<string, WebGLProgram>>();

/**
 * Drop every cached program for a GL context. For `webglcontextrestored`:
 * after a context loss the SAME context object becomes usable again, but every
 * program compiled against it is dead — the WeakMap would happily return those
 * corpses to getOrCreateProgram. Clearing the per-context entry forces fresh
 * compiles on the restored context.
 */
export function clearProgramCache(gl: WebGL2RenderingContext): void {
  PROGRAM_CACHE.delete(gl);
}

/**
 * Get a cached program or compile + link a fresh one. Returns null
 * on compile or link failure (errors logged to console with the
 * cache key for debugging). Caller is responsible for handling the
 * null return; ShaderChargingAnimation falls back to an empty
 * canvas in that case rather than crashing the React tree.
 *
 * Cache lookup is by string equality on `cacheKey`. The compile
 * cost is non-trivial (10-50ms for the orb composite on a mid-tier
 * laptop) so the cache pays out quickly under theme-flip churn.
 */
export function getOrCreateProgram(
  gl: WebGL2RenderingContext,
  cacheKey: string,
  vertexSource: string,
  fragmentSource: string,
): WebGLProgram | null {
  // Resolve (or lazily create) the per-GL-context inner cache. Lazy
  // creation is fine because the WeakMap entry is essentially free
  // until the first program lands in the inner Map. The inner Map
  // lives as long as the gl context lives; when the gl gets GC'd
  // (canvas unmounted, page navigated away), the WeakMap entry
  // drops automatically and the inner Map + its WebGLProgram
  // values follow it into GC.
  let perContextCache = PROGRAM_CACHE.get(gl);
  if (!perContextCache) {
    perContextCache = new Map<string, WebGLProgram>();
    PROGRAM_CACHE.set(gl, perContextCache);
  }
  const cached = perContextCache.get(cacheKey);
  if (cached) return cached;

  const vertexShader = compileShader(gl, gl.VERTEX_SHADER, vertexSource, cacheKey, 'vertex');
  if (!vertexShader) return null;

  const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource, cacheKey, 'fragment');
  if (!fragmentShader) {
    gl.deleteShader(vertexShader);
    return null;
  }

  const program = gl.createProgram();
  if (!program) {
    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);
    // eslint-disable-next-line no-console
    console.error(`[glContext] gl.createProgram returned null [${cacheKey}]`);
    return null;
  }

  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);

  // Shaders are reference-counted by the program after attach;
  // deleting here marks them for cleanup once the program is also
  // deleted (or detached). Standard WebGL hygiene.
  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const info = gl.getProgramInfoLog(program);
    // eslint-disable-next-line no-console
    console.error(`[glContext] program link error [${cacheKey}]:`, info);
    gl.deleteProgram(program);
    return null;
  }

  perContextCache.set(cacheKey, program);
  return program;
}

/**
 * Internal helper. Compiles a single shader stage and returns the
 * WebGLShader handle, or null on compile failure with the error
 * logged. `stage` is included in the error log so callers can
 * distinguish vertex vs fragment failures at a glance.
 */
function compileShader(
  gl: WebGL2RenderingContext,
  type: number,
  source: string,
  cacheKey: string,
  stage: 'vertex' | 'fragment',
): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) {
    // eslint-disable-next-line no-console
    console.error(`[glContext] gl.createShader returned null [${cacheKey} ${stage}]`);
    return null;
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(shader);
    // eslint-disable-next-line no-console
    console.error(`[glContext] ${stage} shader compile error [${cacheKey}]:`, info);
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

// ── Quad buffer ──────────────────────────────────────────────────────

/**
 * Full-screen-quad vertices in normalized device coordinates. Two
 * triangles, 6 vertices, 12 floats. Counterclockwise winding (not
 * that it matters since the shaders don't depend on face
 * orientation, but matching the convention keeps the data
 * shape consistent with the WebGL norm).
 */
const QUAD_VERTICES = new Float32Array([
  -1.0, -1.0,
   1.0, -1.0,
  -1.0,  1.0,
  -1.0,  1.0,
   1.0, -1.0,
   1.0,  1.0,
]);

/**
 * Allocates a buffer, uploads the full-screen quad vertices, and
 * wires up the position attribute. Call once at component mount;
 * the buffer binding state persists across `gl.drawArrays` calls
 * in the same context, so subsequent frames don't need to re-bind.
 *
 * Returns the buffer handle so the caller can dispose it on
 * unmount via `disposeBuffer`. Returns null if buffer allocation
 * fails (rare; usually only on context loss).
 *
 * The default attribute name `'aPos'` matches the vertex shader
 * source from Commit 5. If a future shader uses a different name,
 * pass it explicitly.
 */
export function setupQuadBuffer(
  gl: WebGL2RenderingContext,
  program: WebGLProgram,
  attribName: string = 'aPos',
): WebGLBuffer | null {
  const buffer = gl.createBuffer();
  if (!buffer) {
    // eslint-disable-next-line no-console
    console.error('[glContext] gl.createBuffer returned null');
    return null;
  }
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, QUAD_VERTICES, gl.STATIC_DRAW);

  const attribLocation = gl.getAttribLocation(program, attribName);
  if (attribLocation === -1) {
    // Attribute is inactive in the linked program — unusual but
    // not fatal. The draw call will produce no useful output but
    // won't crash; return the buffer so the caller can still
    // dispose it cleanly.
    // eslint-disable-next-line no-console
    console.warn(`[glContext] attribute '${attribName}' inactive in program`);
    return buffer;
  }
  gl.enableVertexAttribArray(attribLocation);
  gl.vertexAttribPointer(attribLocation, 2, gl.FLOAT, false, 0, 0);
  return buffer;
}

// ── Uniform binding ──────────────────────────────────────────────────

/**
 * Binds a record of uniforms to the active program's uniform
 * locations. Skips uniforms whose location resolves to null (the
 * compiled program optimized them away because they don't affect
 * the output — common for shaders that don't use every declared
 * uniform).
 *
 * Dispatches on value shape: number → uniform1f, length-2 tuple →
 * uniform2f, length-3 → uniform3f, length-4 → uniform4f. Uses the
 * scalar `gl.uniformNf` form rather than `gl.uniformNfv` to avoid
 * the TypedArray allocation that the vector form performs
 * internally; for tuples of known length, the scalar form is
 * meaningfully cheaper at 60 FPS.
 *
 * Caller is responsible for having called `gl.useProgram(program)`
 * before calling this — the active program owns the uniform
 * location lookups.
 */
export function bindUniforms(
  gl: WebGL2RenderingContext,
  program: WebGLProgram,
  uniforms: Record<string, UniformValue>,
): void {
  for (const [name, value] of Object.entries(uniforms)) {
    const loc = gl.getUniformLocation(program, name);
    if (loc === null) continue;
    if (typeof value === 'number') {
      gl.uniform1f(loc, value);
    } else if (value.length === 2) {
      gl.uniform2f(loc, value[0], value[1]);
    } else if (value.length === 3) {
      gl.uniform3f(loc, value[0], value[1], value[2]);
    } else {
      gl.uniform4f(loc, value[0], value[1], value[2], value[3]);
    }
  }
}

// ── Frame rendering ──────────────────────────────────────────────────

/**
 * Renders one frame. Clears, sets the viewport to the canvas
 * backing-store size (handles DPR scaling — the backing store may
 * be larger than the CSS size when DPR > 1), activates the
 * program, binds uniforms, draws the 6-vertex quad.
 *
 * Caller invariants:
 *   - `setupQuadBuffer` was called once at mount with the same
 *     program (the attribute binding state persists across
 *     frames in the same context).
 *   - The canvas backing-store dimensions match the desired
 *     render size (the caller has called canvas.width/height
 *     setters appropriately; this helper just reads them).
 *
 * Clear color is fully transparent black so the canvas composites
 * correctly over any DOM background. The shader output's alpha
 * channel comes from the fragment shaders themselves (they all
 * emit alpha = 1.0 at every pixel, so the clear is mostly a
 * defensive measure for the case where the shader leaves gaps).
 */
export function renderFrame(
  gl: WebGL2RenderingContext,
  program: WebGLProgram,
  uniforms: Record<string, UniformValue>,
): void {
  gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.useProgram(program);
  bindUniforms(gl, program, uniforms);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
}

// ── Disposal ─────────────────────────────────────────────────────────

/**
 * Releases the GL buffer. Call from the consumer's unmount cleanup
 * to free GPU memory. Safe to call multiple times — `gl.deleteBuffer`
 * is a no-op on already-deleted handles.
 */
export function disposeBuffer(gl: WebGL2RenderingContext, buffer: WebGLBuffer): void {
  gl.deleteBuffer(buffer);
}

/**
 * Removes a single program from the per-GL-context cache and
 * calls `gl.deleteProgram` on the underlying GPU resource.
 *
 * Signature change (post-WeakMap rework): `gl` is now REQUIRED
 * because the cache is keyed by GL context — without the gl, the
 * function can't locate which inner Map holds the entry. The
 * pre-rework "optional gl, just drop the cache entry" mode is no
 * longer needed: normal React unmounts let the gl get GC'd,
 * which drops the entire per-context Map (including all its
 * programs) automatically via the WeakMap; no manual
 * disposeProgram call is required at unmount time.
 *
 * Use this for:
 *   - Tests that want to evict a specific program mid-context
 *     to force recompile.
 *   - Hot-reload scenarios where the GLSL source changed but the
 *     gl context stayed alive.
 * Both are rare; the disposeProgram surface is kept for those
 * edge cases but isn't called from any production path today.
 */
export function disposeProgram(gl: WebGL2RenderingContext, cacheKey: string): void {
  const perContextCache = PROGRAM_CACHE.get(gl);
  if (!perContextCache) return;
  const program = perContextCache.get(cacheKey);
  if (!program) return;
  perContextCache.delete(cacheKey);
  gl.deleteProgram(program);
}

/**
 * Deterministically release a canvas's WebGL2 context. Call ONLY on a
 * component's TRUE unmount.
 *
 * WHY THIS EXISTS: browsers cap the number of LIVE WebGL contexts
 * (mobile Safari ~8, desktop Chrome ~16) and reclaim an unmounted
 * canvas's context only on GC — which is non-deterministic and lags
 * the React unmount by an indeterminate amount. A page holding many
 * preview canvases (the /themes grid renders one ChargingAnimation per
 * theme card — 6-9 live WebGL2 contexts) therefore leaves that many
 * contexts pending-GC the moment the user navigates away. If the next
 * screen constructs its own context — the Mapbox map remounts on /map
 * entry (its key includes theme.id, so a theme switch guarantees a
 * fresh construct) — the live count can breach the cap while those
 * orphans are still counted, and the browser evicts/starves a context
 * mid-init: Mapbox's raster tile TEXTURES fail to upload (ocean, a flat
 * clear-colour fill, paints; satellite land tiles and a black
 * framebuffer wedge do not), while its DOM markers (labels, clusters)
 * render fine because they aren't GPU textures. It self-heals once GC
 * finally reclaims the orphans. loseContext() here frees the slot
 * synchronously at unmount, keeping the budget clear for whatever
 * mounts next.
 *
 * MUST be unmount-only: after loseContext() the SAME canvas cannot
 * re-acquire a context without restoreContext(), so calling this on an
 * effect re-run that reuses the canvas (a shader variant flip) would
 * black out the preview. Callers gate it behind an empty-deps effect.
 *
 * Best-effort: WEBGL_lose_context is effectively universal, but a
 * missing extension, an already-lost context, or a torn-down canvas is
 * a silent no-op — losing the context is an optimisation, never a
 * correctness requirement, so it must never throw through unmount.
 */
export function releaseCanvasContext(canvas: HTMLCanvasElement | null): void {
  if (!canvas) return;
  try {
    const gl = canvas.getContext('webgl2');
    gl?.getExtension('WEBGL_lose_context')?.loseContext();
  } catch {
    // Swallow — see JSDoc: best-effort slot release, never fatal.
  }
}

// ── Pausable RAF (Page Visibility API integration) ───────────────────

/**
 * Creates a RAF loop that automatically pauses when the document
 * is hidden (tab backgrounded) and resumes when visible. Critical
 * for charging-session screens that may run for hours — without
 * the pause, a backgrounded tab continues to allocate GPU work
 * the user can't see.
 *
 * Idempotent on both start and stop. Calling start() while
 * already running is a no-op; calling stop() while already
 * stopped is a no-op. The visibility listener attaches on first
 * start and detaches on stop, so the helper leaves no leaked
 * listeners when fully torn down.
 *
 * `tick` runs at display refresh rate while visible. Implementors
 * should keep tick under ~5ms to maintain 60 FPS on lower-tier
 * devices; the shader work dominates the budget so React-side
 * state updates inside tick should be rare.
 *
 * `opts.minFrameMs` caps the tick rate: RAF keeps scheduling at
 * display refresh (so pause/resume semantics are unchanged) but the
 * tick is skipped until at least minFrameMs has elapsed since the
 * last executed tick. The heavy stateful sims (fluid / physarum /
 * marbling / cloth) pass ~33ms for a 30fps cap — the phone is not
 * necessarily plugged in while the CAR charges; the existing
 * lightweight shaders pass nothing and keep running at refresh rate.
 */
export function createPausableRAF(
  tick: () => void,
  opts?: { minFrameMs?: number },
): PausableRAF {
  let rafId: number | null = null;
  let started = false;
  let visibilityListener: (() => void) | null = null;
  const minFrameMs = opts?.minFrameMs ?? 0;
  let lastTickAt = 0;

  const scheduleNext = (): void => {
    rafId = requestAnimationFrame(() => {
      const now = performance.now();
      // Frame cap: skip this refresh cycle if the previous tick ran
      // more recently than the cap allows. A small tolerance (-1ms)
      // keeps a 33ms cap from beating against 16.67ms frames into an
      // effective 20fps.
      if (minFrameMs === 0 || now - lastTickAt >= minFrameMs - 1) {
        lastTickAt = now;
        tick();
      }
      scheduleNext();
    });
  };

  const handleVisibilityChange = (): void => {
    if (document.hidden) {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    } else if (started && rafId === null) {
      // Visibility flipped back to visible while started; resume
      // by scheduling a fresh RAF. The previous rafId was
      // already canceled in the hidden branch.
      scheduleNext();
    }
  };

  const start = (): void => {
    if (started) return;
    started = true;
    visibilityListener = handleVisibilityChange;
    document.addEventListener('visibilitychange', visibilityListener);
    // Skip the initial schedule if the document is hidden at
    // start time (e.g. component mounted in a background tab).
    // The visibility listener will pick up the next visible flip.
    if (!document.hidden) scheduleNext();
  };

  const stop = (): void => {
    if (!started) return;
    started = false;
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    if (visibilityListener) {
      document.removeEventListener('visibilitychange', visibilityListener);
      visibilityListener = null;
    }
  };

  return { start, stop };
}
